import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { usersAPI, storeManagerAPI, notificationsAPI } from '../api';
import {
  SquaresFour as LayoutDashboard,
  ChartBar as BarChart3,
  Users,
  Buildings as Building2,
  Pulse as Activity,
  FileText,
  GearSix as Settings,
  Monitor,
  SignOut as LogOut,
  List as Menu,
  X,
  Crown,
  Sun,
  Moon,
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
  Calendar,
  Clock,
  Package,
  Bell,
  Wallet,
  UserCheck,
  CaretDown as ChevronDown,
  Check,
  Globe,
  Camera,
  Eye,
  EyeSlash as EyeOff,
  FloppyDisk as Save,
  Trash as Trash2,
  WarningCircle as AlertCircle,
  User,
  Lock,
  Envelope as Mail,
  Phone,
  Key as KeyRound,
  Sparkle as Sparkles,
  ArrowsLeftRight as ArrowLeftRight,
  Briefcase,
  UserGear as UserCog,
  Armchair,
  CookingPot as ChefHat,
  ArrowCounterClockwise as RotateCcw,
  ForkKnife as UtensilsCrossed,
  Grains as Wheat,
  DotsThreeOutline,
  Storefront as StorefrontIcon
} from '@phosphor-icons/react';
import SubscriptionBanner from './SubscriptionBanner';

const OwnerLayout = ({ children }) => {
  const { user, logout, isImpersonating, refreshUser } = useAuth();
  const { branches, currentBranch, switchBranch, switchToAllBranches, isAllBranches, getActiveBranches } = useBranch();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('pos-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  // Toggle dark class on html element for Tailwind dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('pos-dark-mode', JSON.stringify(darkMode));
  }, [darkMode]);

  const [currentTime, setCurrentTime] = useState(new Date());

  // Check if currently on POS page - disable branch switching
  const isOnPOSPage = location.pathname.includes('/pos');
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const [showPOSBranchModal, setShowPOSBranchModal] = useState(false);

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: '',
    username: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const profileImageRef = useRef(null);

  // Notification bell state
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifList, setNotifList] = useState([]);
  const [markingAll, setMarkingAll] = useState(false);
  const [stockAlerts, setStockAlerts] = useState({ lowStock: 0, outOfStock: 0 });
  const notifRef = useRef(null);

  // 24-hour snooze helpers (stored in localStorage)
  const SNOOZE_KEY = 'stock-alert-snooze';
  const isSnoozed = (key) => {
    try { const s = JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}'); return s[key] && Date.now() < s[key]; }
    catch { return false; }
  };
  const snooze24h = (key) => {
    try {
      const s = JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}');
      s[key] = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem(SNOOZE_KEY, JSON.stringify(s));
      setStockAlerts((a) => ({ ...a, [key]: 0 }));
    } catch {}
  };

  const fetchNotifs = () => {
    notificationsAPI.list().then((res) => setNotifList(res.data.notifications || [])).catch(() => {});
    storeManagerAPI.getNotifications().then((res) => {
      setStockAlerts({ lowStock: res.data.lowStock || 0, outOfStock: res.data.outOfStock || 0 });
    }).catch(() => {});
  };

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  const handleMarkRead = async (id) => {
    await notificationsAPI.markRead(id).catch(() => {});
    setNotifList((prev) => prev.filter((n) => n.id !== id));
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    await notificationsAPI.markAllRead().catch(() => {});
    setNotifList([]);
    setMarkingAll(false);
  };

  // Close notif dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Get tenant slug for navigation
  const tenantSlug = slug || user?.tenantSlug || '';

  // Handle Launch POS click - show branch selector if in All Branches mode
  const handleLaunchPOS = () => {
    if (isAllBranches) {
      setShowPOSBranchModal(true);
    } else {
      setMobileMenuOpen(false);
      navigate(`/${tenantSlug}/owner/pos`);
    }
  };

  // Navigate to POS with selected branch
  const launchPOSForBranch = (branch) => {
    switchBranch(branch.id);
    setShowPOSBranchModal(false);
    setMobileMenuOpen(false);
    navigate(`/${tenantSlug}/owner/pos`);
  };

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.branch-dropdown')) {
        setBranchDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate(`/${tenantSlug}/login`);
  };

  // Profile handlers
  const openProfileModal = () => {
    setProfileData({
      fullName: user?.fullName || '',
      username: user?.username || '',
      email: user?.email || '',
      phone: user?.phone || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setProfileImage(null);
    setProfileImagePreview(user?.profileImage || null);
    setProfileError('');
    setProfileSuccess('');
    setActiveTab('profile');
    setShowProfileModal(true);
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setProfileError('Image must be less than 5MB');
        return;
      }
      setProfileImage(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUploadProfileImage = async () => {
    if (!profileImage) return;

    setProfileLoading(true);
    setProfileError('');
    try {
      const formData = new FormData();
      formData.append('image', profileImage);
      await usersAPI.uploadProfileImage(formData);
      setProfileSuccess('Profile image updated!');
      setProfileImage(null);
      // Refresh user data would be ideal here
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to upload image');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleDeleteProfileImage = async () => {
    setProfileLoading(true);
    setProfileError('');
    try {
      await usersAPI.deleteProfileImage();
      setProfileImagePreview(null);
      setProfileSuccess('Profile image removed');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to delete image');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileError('');
    setProfileSuccess('');

    // Validate passwords if changing
    if (profileData.newPassword) {
      if (!profileData.currentPassword) {
        setProfileError('Current password is required to change password');
        return;
      }
      if (profileData.newPassword !== profileData.confirmPassword) {
        setProfileError('New passwords do not match');
        return;
      }
      if (profileData.newPassword.length < 6) {
        setProfileError('New password must be at least 6 characters');
        return;
      }
    }

    setProfileLoading(true);
    try {
      const updateData = {};
      if (profileData.fullName !== user?.fullName) {
        updateData.fullName = profileData.fullName;
      }
      if (profileData.username !== user?.username) {
        updateData.username = profileData.username;
      }
      if (profileData.email !== (user?.email || '')) {
        updateData.email = profileData.email;
      }
      if (profileData.phone !== (user?.phone || '')) {
        updateData.phone = profileData.phone;
      }
      if (profileData.newPassword) {
        updateData.currentPassword = profileData.currentPassword;
        updateData.newPassword = profileData.newPassword;
      }

      if (Object.keys(updateData).length === 0) {
        setProfileError('No changes to save');
        setProfileLoading(false);
        return;
      }

      await usersAPI.updateProfile(updateData);
      await refreshUser(); // Refresh user data in context
      setProfileSuccess('Profile updated successfully!');
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setTimeout(() => {
        setProfileSuccess('');
      }, 3000);
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // Active state – exact match for dashboard, prefix match for consolidated routes
  const isActive = (item) => {
    if (item.matchPrefix) {
      return location.pathname.startsWith(item.matchPrefix);
    }
    return location.pathname === item.path;
  };

  // Check if this is a services-type business
  const isServicesType = ['SERVICES', 'SALON'].includes(user?.businessType);
  // Check if this is a restaurant-type business
  const isRestaurantType = ['FOOD_AND_BEVERAGE', 'RESTAURANT'].includes(user?.businessType);

  // Icon wrappers for consistent phosphor rendering
  const DashboardIcon = (props) => <LayoutDashboard size={20} weight="bold" className={props.className} />;
  const SalesIcon = (props) => <BarChart3 size={20} weight="bold" className={props.className} />;
  const ProductsIcon = (props) => isServicesType ? <Briefcase size={20} weight="bold" className={props.className} /> : isRestaurantType ? <UtensilsCrossed size={20} weight="bold" className={props.className} /> : <Package size={20} weight="bold" className={props.className} />;
  const TeamIcon = (props) => <Users size={20} weight="bold" className={props.className} />;
  const CustomersIcon = (props) => <UserCheck size={20} weight="bold" className={props.className} />;
  const FinanceIcon = (props) => <Wallet size={20} weight="bold" className={props.className} />;
  const SettingsIcon = (props) => <Settings size={20} weight="bold" className={props.className} />;
  const StoreNavIcon = (props) => <StorefrontIcon size={20} weight="bold" className={props.className} />;

  // Flat navigation – 8 items, no groups, sub-tabs live inside pages
  const navItems = [
    { path: `/${tenantSlug}/owner/dashboard`, label: 'Dashboard', icon: DashboardIcon, dataTour: 'dashboard' },
    { path: `/${tenantSlug}/owner/sales`, label: 'Sales', icon: SalesIcon, dataTour: 'sales', matchPrefix: `/${tenantSlug}/owner/sales` },
    { path: `/${tenantSlug}/owner/products`, label: isServicesType ? 'Services' : isRestaurantType ? 'Menu' : 'Products', icon: ProductsIcon, dataTour: 'inventory', matchPrefix: `/${tenantSlug}/owner/products` },
    { path: `/${tenantSlug}/owner/team`, label: 'Team', icon: TeamIcon, dataTour: 'staff', matchPrefix: `/${tenantSlug}/owner/team` },
    { path: `/${tenantSlug}/owner/customers`, label: 'Customers', icon: CustomersIcon, matchPrefix: `/${tenantSlug}/owner/customers` },
    { path: `/${tenantSlug}/owner/finance`, label: 'Finance', icon: FinanceIcon, dataTour: 'reports', matchPrefix: `/${tenantSlug}/owner/finance` },
    { path: `/${tenantSlug}/owner/store`, label: 'My Store', icon: StoreNavIcon, matchPrefix: `/${tenantSlug}/owner/store` },
    { path: `/${tenantSlug}/owner/settings`, label: 'Settings', icon: SettingsIcon, dataTour: 'settings', matchPrefix: `/${tenantSlug}/owner/settings` },
  ];

  // Legacy – kept for mobile menu overlay compatibility during transition
  const navGroups = [{ label: null, links: navItems }];

  const bgClass = darkMode ? 'bg-primaryB-900' : 'bg-primaryB-50';
  const surfaceClass = darkMode ? 'bg-primaryB-800' : 'bg-white';
  const textClass = darkMode ? 'text-white' : 'text-primaryB-900';
  const mutedClass = darkMode ? 'text-primaryB-400' : 'text-primaryB-500';
  const borderClass = darkMode ? 'border-primaryB-700/40' : 'border-primaryB-200/60';

  const activeBranches = getActiveBranches();

  return (
    <div data-theme={darkMode ? 'dark' : 'light'} className={`h-screen-dvh overflow-hidden flex ${bgClass} ${textClass} safe-area-top`}>
      {/* Sidebar - Desktop (Full Height) */}
      <aside className={`hidden lg:flex ${sidebarCollapsed ? 'w-20' : 'w-64'} ${surfaceClass} flex-col shrink-0 transition-all duration-300 overflow-hidden shadow-card dark:shadow-card-dark`}>
        {/* Sidebar Top: Branding + Collapse */}
        <div className={`shrink-0 h-14 sm:h-16 short:h-12 border-b ${borderClass} flex items-center justify-between ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
          <div className={`flex items-center ${sidebarCollapsed ? '' : 'gap-2.5'} min-w-0`}>
            <div className={`w-8 h-8 ${darkMode ? 'bg-primaryB-700 text-primaryA-400' : 'bg-primaryB-950 text-primaryA-300'} rounded-xl flex items-center justify-center shadow-card shrink-0 short:w-7 short:h-7`}>
              <Crown className="w-4 h-4 short:w-3.5 short:h-3.5" />
            </div>
            {!sidebarCollapsed && (
              <div className="min-w-0">
                <h1 className={`text-sm font-bold tracking-tight uppercase ${textClass} truncate`}>Owner Portal</h1>
                <p className="text-[9px] font-bold text-primaryA-500 uppercase tracking-wider truncate mt-0.5">{user?.tenantName}</p>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`shrink-0 p-1.5 rounded-xl ${mutedClass} hover:bg-primaryB-50 dark:hover:bg-primaryB-700 transition-all duration-200`}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>

        {/* Branch Selector */}
        {!sidebarCollapsed && (
          <div className={`shrink-0 px-3 py-3 border-b ${borderClass}`}>
            <div className="relative branch-dropdown">
              <button
                onClick={() => !isOnPOSPage && setBranchDropdownOpen(!branchDropdownOpen)}
                disabled={isOnPOSPage}
                title={isOnPOSPage ? 'Exit POS to change branch' : ''}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl border ${borderClass} ${surfaceClass} ${textClass} text-sm font-bold ${isOnPOSPage ? 'opacity-60 cursor-not-allowed' : 'hover:bg-primaryB-50 dark:hover:bg-primaryB-700'} transition-all duration-200`}
              >
                <Building2 className={`w-4 h-4 shrink-0 ${darkMode ? 'text-primaryA-400' : 'text-primaryB-600'}`} />
                <span className="truncate flex-1 text-left">
                  {isAllBranches ? 'All Branches' : currentBranch?.name || 'Select Branch'}
                </span>
                {isOnPOSPage ? (
                  <Lock className="w-4 h-4 text-amber-500 shrink-0" />
                ) : (
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${branchDropdownOpen ? 'rotate-180' : ''}`} />
                )}
              </button>

              {branchDropdownOpen && (
                <div className={`absolute left-0 top-full mt-2 w-full ${surfaceClass} rounded-2xl border ${borderClass} shadow-elevated dark:shadow-elevated-dark z-50 overflow-hidden`}>
                  <div className={`px-4 py-2 border-b ${borderClass}`}>
                    <p className={`text-xs font-bold uppercase ${mutedClass}`}>Switch Branch</p>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <button
                      onClick={() => { switchToAllBranches(); setBranchDropdownOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-primaryB-50 dark:hover:bg-primaryB-700 transition-all duration-200 ${isAllBranches ? 'bg-primaryB-100 dark:bg-primaryB-700' : ''}`}
                    >
                      <Globe className={`w-4 h-4 ${isAllBranches ? 'text-primaryB-700 dark:text-primaryA-400' : mutedClass}`} />
                      <div className="flex-1 text-left">
                        <p className={`text-sm font-bold ${textClass}`}>All Branches</p>
                        <p className={`text-xs ${mutedClass}`}>View combined data</p>
                      </div>
                      {isAllBranches && <Check className="w-4 h-4 text-primaryB-700 dark:text-primaryA-400" />}
                    </button>
                    {activeBranches.map(branch => (
                      <button
                        key={branch.id}
                        onClick={() => { switchBranch(branch.id); setBranchDropdownOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-primaryB-50 dark:hover:bg-primaryB-700 transition-all duration-200 ${currentBranch?.id === branch.id ? 'bg-primaryB-100 dark:bg-primaryB-700' : ''}`}
                      >
                        <Building2 className={`w-4 h-4 ${currentBranch?.id === branch.id ? 'text-primaryB-700 dark:text-primaryA-400' : mutedClass}`} />
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-bold ${textClass}`}>{branch.name}</p>
                            {branch.isMain && (
                              <span className="px-1.5 py-0.5 bg-primaryB-200 dark:bg-primaryB-600 text-primaryB-700 dark:text-primaryB-200 text-[9px] font-bold uppercase rounded">Main</span>
                            )}
                          </div>
                          <p className={`text-xs ${mutedClass}`}>{branch._count?.users || 0} staff</p>
                        </div>
                        {currentBranch?.id === branch.id && <Check className="w-4 h-4 text-primaryB-700 dark:text-primaryA-400" />}
                      </button>
                    ))}
                  </div>
                  <div className={`px-4 py-2 border-t ${borderClass}`}>
                    <Link to={`/${tenantSlug}/owner/settings/branches`} onClick={() => setBranchDropdownOpen(false)}
                      className={`text-xs ${darkMode ? 'text-primaryA-400' : 'text-primaryB-600'} hover:underline font-bold uppercase`}
                    >Manage Branches</Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

          {/* Nav – flat list, 7 items */}
          <nav className="flex-1 overflow-y-auto py-3 px-2">
            <div className="space-y-0.5">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                return (
                  <Link key={item.path} to={item.path} data-tour={item.dataTour}
                    className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-xl text-xs font-bold uppercase transition-all duration-200 ${
                      active
                        ? darkMode ? 'bg-primaryA-500/15 text-primaryA-400' : 'bg-[#373a3f] text-primaryA-400 shadow-elevated'
                        : `${mutedClass} ${darkMode ? 'hover:bg-primaryB-700 hover:text-white' : 'hover:bg-primaryB-50 hover:text-primaryB-900'}`
                    }`}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Footer */}
          <div className={`shrink-0 border-t ${borderClass} p-2.5 space-y-1`}>
            {!sidebarCollapsed && (
              <div className={`flex items-center rounded-xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} p-1 mb-1`}>
                <button
                  onClick={() => setDarkMode(false)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${!darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
                >
                  <Sun weight="bold" className="w-3.5 h-3.5" />
                  Light
                </button>
                <button
                  onClick={() => setDarkMode(true)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
                >
                  <Moon weight="bold" className="w-3.5 h-3.5" />
                  Dark
                </button>
              </div>
            )}
            {sidebarCollapsed && (
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`w-full flex items-center justify-center p-2 rounded-xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} ${textClass} mb-1`}
                title={darkMode ? 'Switch to Light' : 'Switch to Dark'}
              >
                {darkMode ? <Sun weight="bold" className="w-4 h-4" /> : <Moon weight="bold" className="w-4 h-4" />}
              </button>
            )}
            <button onClick={handleLaunchPOS} data-tour="pos"
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-xl bg-accent-500 hover:bg-accent-600 text-white text-xs font-bold uppercase shadow-elevated shadow-accent-500/20 transition-all duration-200`}
              title={sidebarCollapsed ? 'Launch POS' : ''}
            >
              <Monitor className="w-5 h-5 shrink-0" />
              {!sidebarCollapsed && <span>Launch POS</span>}
            </button>
            <button onClick={handleLogout}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-xl text-xs font-bold uppercase ${mutedClass} hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200`}
              title={sidebarCollapsed ? 'Logout' : ''}
            >
              <LogOut className="w-5 h-5 shrink-0" />
              {!sidebarCollapsed && <span>Logout</span>}
            </button>
          </div>
        </aside>

      {/* Right Side: Header + Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Subscription Warning Banner */}
        <SubscriptionBanner />

        {/* Header (right side only) */}
        <header className={`h-14 sm:h-16 short:h-12 ${darkMode ? 'bg-primaryB-900/60' : 'bg-[#F7F7F5]/80'} backdrop-blur-xl border-b ${darkMode ? 'border-primaryB-800' : 'border-primaryB-200/40'} flex items-center justify-between px-4 sm:px-6 lg:px-8 short:px-3 shrink-0 z-10`}>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`hidden p-2 ${mutedClass} hover:${textClass} rounded-lg`}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            {/* Branch Selector - Header (mobile/tablet only) */}
            <div className="lg:hidden relative branch-dropdown">
              <button
                onClick={() => !isOnPOSPage && setBranchDropdownOpen(!branchDropdownOpen)}
                disabled={isOnPOSPage}
                title={isOnPOSPage ? 'Exit POS to change branch' : ''}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border ${borderClass} ${surfaceClass} ${textClass} text-xs font-bold ${isOnPOSPage ? 'opacity-60 cursor-not-allowed' : 'hover:bg-primaryB-50 dark:hover:bg-primaryB-700'} transition-all duration-200`}
              >
                <Building2 className={`w-3.5 h-3.5 shrink-0 ${darkMode ? 'text-primaryA-400' : 'text-primaryB-600'}`} />
                <span className="truncate max-w-[120px] sm:max-w-[180px]">
                  {isAllBranches ? 'All Branches' : currentBranch?.name || 'Select Branch'}
                </span>
                {isOnPOSPage ? (
                  <Lock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                ) : (
                  <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${branchDropdownOpen ? 'rotate-180' : ''}`} />
                )}
              </button>
              {branchDropdownOpen && (
                <div className={`absolute left-0 top-full mt-2 w-56 ${surfaceClass} rounded-2xl border ${borderClass} shadow-elevated dark:shadow-elevated-dark z-50 overflow-hidden`}>
                  <div className="max-h-64 overflow-y-auto">
                    <button
                      onClick={() => { switchToAllBranches(); setBranchDropdownOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primaryB-50 dark:hover:bg-primaryB-700 transition-all duration-200 ${isAllBranches ? 'bg-primaryB-100 dark:bg-primaryB-700' : ''}`}
                    >
                      <Globe className={`w-4 h-4 ${isAllBranches ? 'text-primaryA-400' : mutedClass}`} />
                      <span className={`text-sm font-bold flex-1 text-left ${textClass}`}>All Branches</span>
                      {isAllBranches && <Check className="w-4 h-4 text-primaryA-400" />}
                    </button>
                    {activeBranches.map(branch => (
                      <button
                        key={branch.id}
                        onClick={() => { switchBranch(branch.id); setBranchDropdownOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primaryB-50 dark:hover:bg-primaryB-700 transition-all duration-200 ${currentBranch?.id === branch.id ? 'bg-primaryB-100 dark:bg-primaryB-700' : ''}`}
                      >
                        <Building2 className={`w-4 h-4 ${currentBranch?.id === branch.id ? 'text-primaryA-400' : mutedClass}`} />
                        <span className={`text-sm font-bold flex-1 text-left ${textClass}`}>{branch.name}</span>
                        {currentBranch?.id === branch.id && <Check className="w-4 h-4 text-primaryA-400" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Date/Time Display */}
            <div className="hidden lg:flex items-center gap-2">
              <span className={`text-[11px] font-medium tracking-wide ${mutedClass}`}>
                {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
              <span className={`text-[11px] ${darkMode ? 'text-primaryB-600' : 'text-primaryB-300'}`}>·</span>
              <span className={`text-[11px] font-medium tracking-wide ${mutedClass}`}>
                {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl hidden sm:flex items-center justify-center ${mutedClass} hover:${textClass} ${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} transition-all duration-200`}
            >
              {darkMode ? <Sun className="w-4 h-4 sm:w-[18px] sm:h-[18px]" /> : <Moon className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />}
            </button>
            {/* Notification Bell */}
            <div ref={notifRef} className="relative">
              {(() => {
                const unread = notifList.filter((n) => !n.isRead).length
                  + (!isSnoozed('lowStock') && stockAlerts.lowStock > 0 ? 1 : 0)
                  + (!isSnoozed('outOfStock') && stockAlerts.outOfStock > 0 ? 1 : 0);
                const NOTIF_ICONS = {
                  STOREFRONT_ORDER: { emoji: '🛍️', bg: 'bg-blue-100 dark:bg-blue-900/30' },
                  VOID_REQUEST:     { emoji: '⚠️', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
                  LOW_STOCK:        { emoji: '📦', bg: 'bg-orange-100 dark:bg-orange-900/30' },
                  OUT_OF_STOCK:     { emoji: '🚫', bg: 'bg-red-100 dark:bg-red-900/30' },
                };
                const timeAgo = (d) => {
                  const m = Math.floor((Date.now() - new Date(d)) / 60000);
                  if (m < 1) return 'just now';
                  if (m < 60) return `${m}m ago`;
                  const h = Math.floor(m / 60);
                  if (h < 24) return `${h}h ago`;
                  return `${Math.floor(h / 24)}d ago`;
                };
                return (
                  <>
                    <button
                      onClick={() => { setNotifOpen((o) => !o); if (!notifOpen) fetchNotifs(); }}
                      className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center relative ${mutedClass} hover:${textClass} ${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} transition-all duration-200`}
                      title="Notifications"
                    >
                      <Bell className="w-4 h-4 sm:w-[18px] sm:h-[18px]" weight={unread > 0 ? 'fill' : 'bold'} />
                      {unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                          {unread > 9 ? '9+' : unread}
                        </span>
                      )}
                    </button>

                    {notifOpen && (
                      <div className={`fixed top-[60px] inset-x-2 sm:absolute sm:top-auto sm:inset-x-auto sm:right-0 sm:w-80 sm:mt-2 ${surfaceClass} border ${borderClass} rounded-2xl shadow-elevated dark:shadow-elevated-dark z-50 overflow-hidden`}>
                        <div className={`px-4 py-3 border-b ${borderClass} flex items-center justify-between`}>
                          <p className={`text-sm font-bold ${textClass}`}>Notifications</p>
                          {unread > 0 && (
                            <button
                              onClick={handleMarkAllRead}
                              disabled={markingAll}
                              className="text-xs font-semibold text-primaryA-500 hover:text-primaryA-600 transition-colors disabled:opacity-50"
                            >
                              Mark all read
                            </button>
                          )}
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                          {/* Stock alerts (snoozeable) */}
                          {!isSnoozed('lowStock') && stockAlerts.lowStock > 0 && (
                            <div className={`flex items-center gap-3 px-4 py-3 border-b ${borderClass}`}>
                              <span className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center flex-shrink-0 text-base">📦</span>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-semibold ${textClass}`}>{stockAlerts.lowStock} Low Stock</p>
                                <p className={`text-xs ${mutedClass}`}>Below reorder threshold</p>
                              </div>
                              <button onClick={() => snooze24h('lowStock')} className={`text-[10px] font-semibold px-2 py-1 ${mutedClass} hover:text-neutral-700 dark:hover:text-neutral-300 flex-shrink-0`}>Snooze 24h</button>
                            </div>
                          )}
                          {!isSnoozed('outOfStock') && stockAlerts.outOfStock > 0 && (
                            <div className={`flex items-center gap-3 px-4 py-3 border-b ${borderClass}`}>
                              <span className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center flex-shrink-0 text-base">🚫</span>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-semibold ${textClass}`}>{stockAlerts.outOfStock} Out of Stock</p>
                                <p className={`text-xs ${mutedClass}`}>No inventory remaining</p>
                              </div>
                              <button onClick={() => snooze24h('outOfStock')} className={`text-[10px] font-semibold px-2 py-1 ${mutedClass} hover:text-neutral-700 dark:hover:text-neutral-300 flex-shrink-0`}>Snooze 24h</button>
                            </div>
                          )}
                          {notifList.length === 0 ? (
                            <div className="px-4 py-6 text-center">
                              <p className={`text-sm ${mutedClass}`}>All clear – no notifications yet</p>
                            </div>
                          ) : (
                            notifList.map((n) => {
                              const cfg = NOTIF_ICONS[n.type] || { emoji: '🔔', bg: 'bg-neutral-100 dark:bg-neutral-800' };
                              return (
                                <button
                                  key={n.id}
                                  onClick={() => {
                                    handleMarkRead(n.id);
                                    setNotifOpen(false);
                                    if (n.link) navigate(`/${tenantSlug}${n.link}`);
                                  }}
                                  className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-primaryB-50 dark:hover:bg-primaryB-800 ${!n.isRead ? (darkMode ? 'bg-primaryB-900' : 'bg-primaryA-50/50') : ''}`}
                                >
                                  <span className={`w-8 h-8 ${cfg.bg} rounded-xl flex items-center justify-center flex-shrink-0 text-base mt-0.5`}>{cfg.emoji}</span>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <p className={`text-sm font-semibold ${textClass} truncate flex-1`}>{n.title}</p>
                                      {!n.isRead && <span className="w-2 h-2 bg-primaryA-500 rounded-full flex-shrink-0" />}
                                    </div>
                                    <p className={`text-xs ${mutedClass} truncate`}>{n.message}</p>
                                    <p className={`text-[10px] ${mutedClass} mt-0.5`}>{timeAgo(n.createdAt)}</p>
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <button
              onClick={openProfileModal}
              className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full border-2 ${darkMode ? 'border-primaryB-600' : 'border-primaryB-200'} bg-primaryB-100 dark:bg-primaryB-700 overflow-hidden hover:border-primaryA-400 transition-all duration-200`}
              title="Edit Profile"
            >
              {user?.profileImage ? (
                <img src={user.profileImage} alt={user.fullName} className="w-full h-full object-cover" />
              ) : (
                <div className={`w-full h-full ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-200'} flex items-center justify-center text-xs font-bold`}>
                  {user?.fullName?.charAt(0) || 'O'}
                </div>
              )}
            </button>
          </div>
        </header>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
            <div className={`w-72 h-full ${surfaceClass} flex flex-col overflow-hidden`} onClick={(e) => e.stopPropagation()}>
              <div className={`flex items-center gap-3 px-5 border-b ${borderClass} pt-6 pb-4 shrink-0`}>
                <div className={`w-8 h-8 ${darkMode ? 'bg-primaryB-700 text-primaryA-400' : 'bg-primaryB-950 text-primaryA-300'} rounded-xl flex items-center justify-center shadow-card`}>
                  <Crown className="w-4 h-4" />
                </div>
                <div>
                  <p className={`text-sm font-bold tracking-tight uppercase ${textClass}`}>Owner Portal</p>
                  <p className="text-[9px] font-bold text-primaryA-500 uppercase tracking-wider">{user?.tenantName}</p>
                </div>
              </div>

              {/* Branch Selector - Mobile */}
              <div className={`shrink-0 px-3 py-3 border-b ${borderClass}`}>
                <div className="relative branch-dropdown">
                  <button
                    onClick={() => setBranchDropdownOpen(!branchDropdownOpen)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl border ${borderClass} ${surfaceClass} ${textClass} text-sm font-bold hover:bg-primaryB-50 dark:hover:bg-primaryB-700 transition-all duration-200`}
                  >
                    <Building2 className={`w-4 h-4 shrink-0 ${darkMode ? 'text-primaryA-400' : 'text-primaryB-600'}`} />
                    <span className="truncate flex-1 text-left">
                      {isAllBranches ? 'All Branches' : currentBranch?.name || 'Select Branch'}
                    </span>
                    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${branchDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {branchDropdownOpen && (
                    <div className={`absolute left-0 top-full mt-2 w-full ${surfaceClass} rounded-2xl border ${borderClass} shadow-elevated dark:shadow-elevated-dark z-50 overflow-hidden`}>
                      <div className="max-h-48 overflow-y-auto">
                        <button
                          onClick={() => { switchToAllBranches(); setBranchDropdownOpen(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-primaryB-50 dark:hover:bg-primaryB-700 transition-all duration-200 ${isAllBranches ? 'bg-primaryB-100 dark:bg-primaryB-700' : ''}`}
                        >
                          <Globe className={`w-4 h-4 ${isAllBranches ? 'text-primaryA-400' : mutedClass}`} />
                          <span className={`text-sm font-bold flex-1 text-left ${textClass}`}>All Branches</span>
                          {isAllBranches && <Check className="w-4 h-4 text-primaryA-400" />}
                        </button>
                        {activeBranches.map(branch => (
                          <button
                            key={branch.id}
                            onClick={() => { switchBranch(branch.id); setBranchDropdownOpen(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-primaryB-50 dark:hover:bg-primaryB-700 transition-all duration-200 ${currentBranch?.id === branch.id ? 'bg-primaryB-100 dark:bg-primaryB-700' : ''}`}
                          >
                            <Building2 className={`w-4 h-4 ${currentBranch?.id === branch.id ? 'text-primaryA-400' : mutedClass}`} />
                            <span className={`text-sm font-bold flex-1 text-left ${textClass}`}>{branch.name}</span>
                            {currentBranch?.id === branch.id && <Check className="w-4 h-4 text-primaryA-400" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <nav className="flex-1 overflow-y-auto py-3 px-3">
                <div className="space-y-0.5">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item);
                    return (
                      <Link key={item.path} to={item.path} data-tour={item.dataTour} onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold uppercase transition-all duration-200 ${
                          active
                            ? darkMode ? 'bg-primaryB-700 text-primaryA-400' : 'bg-[#373a3f] text-primaryA-400 shadow-elevated'
                            : `${mutedClass} ${darkMode ? 'hover:bg-primaryB-700 hover:text-white' : 'hover:bg-primaryB-50 hover:text-primaryB-900'}`
                        }`}
                      >
                        <Icon className="w-5 h-5" /><span className="truncate">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </nav>
              <div className={`shrink-0 border-t ${borderClass} p-3 space-y-1`}>
                <div className={`flex items-center rounded-xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} p-1 mb-1`}>
                  <button
                    onClick={() => setDarkMode(false)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${!darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
                  >
                    <Sun weight="bold" className="w-3.5 h-3.5" />
                    Light
                  </button>
                  <button
                    onClick={() => setDarkMode(true)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
                  >
                    <Moon weight="bold" className="w-3.5 h-3.5" />
                    Dark
                  </button>
                </div>
                <button onClick={handleLaunchPOS} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-accent-500 hover:bg-accent-600 text-white text-xs font-bold uppercase shadow-elevated shadow-accent-500/20 transition-all duration-200">
                  <Monitor className="w-5 h-5" /><span>Launch POS</span>
                </button>
                <button onClick={handleLogout} className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold uppercase ${mutedClass} hover:text-red-500 hover:bg-red-50 transition-all duration-200`}>
                  <LogOut className="w-5 h-5" /><span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-amber-500 text-amber-900 px-4 py-2 text-sm font-bold flex items-center justify-center gap-2 shrink-0">
            <UserCheck className="w-4 h-4 shrink-0" />
            <span className="truncate">Viewing as tenant — all actions are logged by SmartPOS Admin</span>
            <button
              onClick={logout}
              className="ml-2 shrink-0 px-3 py-1 bg-amber-900 text-amber-100 rounded-lg text-xs hover:bg-amber-950 transition-colors"
            >
              Exit Session
            </button>
          </div>
        )}

        {/* Main Content Area */}
        <main
          id="main-content"
          className={`flex-1 ${notifOpen ? 'pointer-events-none' : ''} ${location.pathname.includes('/pos') ? 'overflow-hidden p-0' : 'overflow-y-auto p-4 pb-32 sm:p-6 sm:pb-32 lg:p-8 lg:pb-0 short:p-3 short:pb-32 short:lg:pb-0 page-gradient-warm'}`}
        >
          {React.cloneElement(children, {
            darkMode,
            surfaceClass,
            textClass,
            mutedClass,
            borderClass,
            bgClass,
            currentBranch,
            isAllBranches,
            branches: getActiveBranches()
          })}
        </main>

        {/* Mobile Bottom Tab Bar */}
        <div className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 ${surfaceClass} border-t ${borderClass} shadow-elevated dark:shadow-elevated-dark safe-area-bottom`}>
          <div className="flex items-center justify-around px-1 py-1.5">
            {[
              { path: `/${tenantSlug}/owner/dashboard`, label: 'Home', icon: LayoutDashboard },
              { path: `/${tenantSlug}/owner/sales`, label: 'Sales', icon: BarChart3, matchPrefix: `/${tenantSlug}/owner/sales` },
              { path: 'pos', label: 'POS', icon: Monitor, action: handleLaunchPOS, highlight: isOnPOSPage },
              { path: `/${tenantSlug}/owner/products`, label: isRestaurantType ? 'Menu' : isServicesType ? 'Services' : 'Products', icon: Package, matchPrefix: `/${tenantSlug}/owner/products` },
              { path: 'more', label: 'More', icon: DotsThreeOutline, action: () => setMoreSheetOpen(true) },
            ].map((tab) => {
              const isTabActive = tab.highlight || (tab.path !== 'pos' && tab.path !== 'more' && isActive(tab));
              return (
                <button
                  key={tab.label}
                  onClick={() => {
                    if (tab.action) {
                      tab.action();
                    } else {
                      navigate(tab.path);
                    }
                  }}
                  className={`flex flex-col items-center justify-center flex-1 py-1.5 rounded-xl transition-all duration-200 ${
                    isTabActive
                      ? darkMode ? 'text-primaryA-400' : 'text-[#373A3F]'
                      : mutedClass
                  }`}
                >
                  <tab.icon weight={isTabActive ? 'fill' : 'bold'} className="w-[22px] h-[22px]" />
                  <span className={`text-[10px] font-bold mt-0.5 ${isTabActive ? (darkMode ? 'text-primaryA-400' : 'text-[#373A3F]') : ''}`}>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* More Bottom Sheet */}
        {moreSheetOpen && createPortal(
          <div className="lg:hidden fixed inset-0 z-[60]" onClick={() => setMoreSheetOpen(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div
              className={`absolute bottom-0 left-0 right-0 ${surfaceClass} rounded-t-3xl max-h-[70vh] overflow-y-auto safe-area-bottom`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center pt-3 pb-2">
                <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-primaryB-600' : 'bg-primaryB-300'}`} />
              </div>
              <div className="px-5 pb-6">
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {navItems.filter(item => {
                    // Exclude items already in the tab bar (Dashboard, Sales, Products)
                    const tabPaths = [`/${tenantSlug}/owner/dashboard`, `/${tenantSlug}/owner/sales`, `/${tenantSlug}/owner/products`];
                    return !tabPaths.includes(item.path);
                  }).map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item);
                    return (
                      <button
                        key={item.path}
                        onClick={() => { navigate(item.path); setMoreSheetOpen(false); }}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-all duration-200 ${
                          active
                            ? `${darkMode ? 'bg-primaryB-700 text-primaryA-400' : 'bg-primaryB-100 text-[#373A3F]'}`
                            : `${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} ${mutedClass}`
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-[10px] font-bold text-center leading-tight">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className={`border-t ${borderClass} pt-3 space-y-2`}>
                  {/* Dark Mode Toggle */}
                  <div className={`flex items-center rounded-xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} p-1`}>
                    <button
                      onClick={() => setDarkMode(false)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${!darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
                    >
                      <Sun weight="bold" className="w-3.5 h-3.5" />
                      Light
                    </button>
                    <button
                      onClick={() => setDarkMode(true)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
                    >
                      <Moon weight="bold" className="w-3.5 h-3.5" />
                      Dark
                    </button>
                  </div>
                  {/* Logout */}
                  <button
                    onClick={() => { setMoreSheetOpen(false); handleLogout(); }}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold uppercase text-negative-500 ${darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'} transition-all duration-200`}
                  >
                    <LogOut weight="bold" className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        , document.body)}
      </div>

      {/* POS Branch Selection Modal */}
      {showPOSBranchModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setShowPOSBranchModal(false)}>
          <div
            className={`${surfaceClass} w-full max-w-md rounded-3xl p-6 sm:p-8 shadow-elevated dark:shadow-elevated-dark border ${borderClass}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className={`w-16 h-16 mx-auto rounded-3xl ${darkMode ? 'bg-accent-500/20' : 'bg-accent-50'} flex items-center justify-center mb-4`}>
                <Monitor className="w-8 h-8 text-accent-500" />
              </div>
              <h2 className={`text-xl font-bold uppercase ${textClass}`}>Select Branch for POS</h2>
              <p className={`text-sm ${mutedClass} mt-2`}>Choose which branch you want to operate the POS terminal for</p>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {activeBranches.map((branch) => (
                <button
                  key={branch.id}
                  onClick={() => launchPOSForBranch(branch)}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border ${borderClass} hover:border-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-all duration-200 text-left group`}
                >
                  <div className={`w-12 h-12 rounded-2xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} flex items-center justify-center group-hover:bg-accent-100 dark:group-hover:bg-accent-900/30 transition-all duration-200`}>
                    <Building2 className={`w-6 h-6 ${mutedClass} group-hover:text-accent-500`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-bold ${textClass}`}>{branch.name}</p>
                    {branch.isMain && (
                      <span className="text-[10px] font-bold uppercase text-accent-500">Main Branch</span>
                    )}
                  </div>
                  <ChevronRight className={`w-5 h-5 ${mutedClass} group-hover:text-accent-500 transition-colors`} />
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowPOSBranchModal(false)}
              className={`w-full mt-4 py-3 rounded-xl text-sm font-bold ${mutedClass} hover:bg-primaryB-100 dark:hover:bg-primaryB-700 transition-all duration-200`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Premium Owner Profile Modal */}
      {showProfileModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
          onClick={() => setShowProfileModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-primaryB-900/70 backdrop-blur-md" />

          {/* Modal */}
          <div
            className={`relative w-full max-w-lg ${surfaceClass} rounded-3xl shadow-elevated dark:shadow-elevated-dark border ${borderClass} max-h-[90vh] overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Premium Header with Gradient */}
            <div className="relative overflow-hidden">
              <div className={`absolute inset-0 ${darkMode ? 'bg-gradient-to-br from-amber-600 via-orange-500 to-rose-500' : 'bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400'}`} />
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />

              <div className="relative px-5 pt-4 pb-12">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-primaryB-50/20 backdrop-blur-sm flex items-center justify-center">
                      <Crown className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white tracking-tight">Owner Profile</h2>
                      <p className="text-white/70 text-[11px] font-semibold">{user?.tenantName}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowProfileModal(false)}
                    className="w-8 h-8 rounded-lg bg-primaryB-50/10 hover:bg-primaryB-50/20 backdrop-blur-sm flex items-center justify-center text-white transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Profile Image */}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
                <div className="relative group">
                  <div className={`w-20 h-20 rounded-3xl border-3 ${darkMode ? 'border-primaryB-800' : 'border-white'} shadow-elevated overflow-hidden bg-gradient-to-br from-amber-400 to-orange-500`}>
                    {profileImagePreview ? (
                      <img src={profileImagePreview} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">
                          {user?.fullName?.charAt(0) || 'O'}
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => profileImageRef.current?.click()}
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-xl bg-amber-500 hover:bg-amber-600 text-white shadow-elevated shadow-amber-500/50 flex items-center justify-center transition-all duration-200 hover:scale-110"
                  >
                    <Camera className="w-3.5 h-3.5" />
                  </button>
                  <input
                    type="file"
                    ref={profileImageRef}
                    onChange={handleProfileImageChange}
                    accept="image/*"
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="pt-14 px-4 sm:px-6 pb-4 overflow-y-auto flex-1">
              {/* Image actions */}
              {profileImage && (
                <div className="flex justify-center gap-2 mb-4">
                  <button
                    onClick={handleUploadProfileImage}
                    disabled={profileLoading}
                    className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl text-xs font-bold shadow-elevated disabled:opacity-50 flex items-center gap-1.5 transition-all duration-200"
                  >
                    <Save className="w-3.5 h-3.5" />
                    {profileLoading ? 'Uploading...' : 'Save Image'}
                  </button>
                  <button
                    onClick={() => {
                      setProfileImage(null);
                      setProfileImagePreview(user?.profileImage || null);
                    }}
                    className={`px-4 py-2 ${surfaceClass} border ${borderClass} rounded-xl text-xs font-bold ${textClass} ${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} transition-all duration-200`}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {profileImagePreview && !profileImage && user?.profileImage && (
                <div className="flex justify-center mb-4">
                  <button
                    onClick={handleDeleteProfileImage}
                    disabled={profileLoading}
                    className="px-3 py-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl text-xs font-bold disabled:opacity-50 flex items-center gap-1.5 transition-all duration-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Remove Image
                  </button>
                </div>
              )}

              {/* Messages */}
              {profileError && (
                <div className="mb-4 p-3 rounded-xl bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                  <p className="text-xs font-semibold text-red-600 dark:text-red-400">{profileError}</p>
                </div>
              )}
              {profileSuccess && (
                <div className="mb-4 p-3 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{profileSuccess}</p>
                </div>
              )}

              {/* Tab Navigation */}
              <div className={`flex gap-1 p-1 rounded-xl ${darkMode ? 'bg-primaryB-700/50' : 'bg-primaryB-100'} mb-4`}>
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all duration-200 ${
                    activeTab === 'profile'
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-elevated'
                      : `${textClass} hover:bg-primaryB-50 dark:hover:bg-primaryB-600`
                  }`}
                >
                  <User className="w-3.5 h-3.5" />
                  Profile
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all duration-200 ${
                    activeTab === 'security'
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-elevated'
                      : `${textClass} hover:bg-primaryB-50 dark:hover:bg-primaryB-600`
                  }`}
                >
                  <Lock className="w-3.5 h-3.5" />
                  Security
                </button>
              </div>

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-3">
                  {/* Full Name */}
                  <div>
                    <label className={`text-[10px] font-bold uppercase ${mutedClass} block mb-1 flex items-center gap-1.5`}>
                      <User className="w-3 h-3" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={profileData.fullName}
                      onChange={(e) => setProfileData(prev => ({ ...prev, fullName: e.target.value }))}
                      className={`w-full px-3 py-2.5 rounded-xl border ${borderClass} ${surfaceClass} ${textClass} text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10 transition-all outline-none`}
                      placeholder="Enter your full name"
                    />
                  </div>

                  {/* Username */}
                  <div>
                    <label className={`text-[10px] font-bold uppercase ${mutedClass} block mb-1 flex items-center gap-1.5`}>
                      <span className="text-amber-500">@</span>
                      Username
                    </label>
                    <input
                      type="text"
                      value={profileData.username}
                      onChange={(e) => setProfileData(prev => ({ ...prev, username: e.target.value }))}
                      className={`w-full px-3 py-2.5 rounded-xl border ${borderClass} ${surfaceClass} ${textClass} text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10 transition-all outline-none`}
                      placeholder="Enter username"
                    />
                  </div>

                  {/* Email & Phone Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={`text-[10px] font-bold uppercase ${mutedClass} block mb-1 flex items-center gap-1.5`}>
                        <Mail className="w-3 h-3" />
                        Email
                      </label>
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                        className={`w-full px-3 py-2.5 rounded-xl border ${borderClass} ${surfaceClass} ${textClass} text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10 transition-all outline-none`}
                        placeholder="Enter email"
                      />
                    </div>
                    <div>
                      <label className={`text-[10px] font-bold uppercase ${mutedClass} block mb-1 flex items-center gap-1.5`}>
                        <Phone className="w-3 h-3" />
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                        className={`w-full px-3 py-2.5 rounded-xl border ${borderClass} ${surfaceClass} ${textClass} text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10 transition-all outline-none`}
                        placeholder="Enter phone"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="space-y-3">
                  {/* Security Notice */}
                  <div className={`p-3 rounded-xl ${darkMode ? 'bg-amber-900/30 border-amber-700/50' : 'bg-amber-50 border-amber-200'} border flex items-center gap-2.5`}>
                    <KeyRound className="w-4 h-4 text-amber-600 shrink-0" />
                    <p className={`text-xs ${darkMode ? 'text-amber-400/80' : 'text-amber-700'}`}>
                      Use at least 6 characters for your password.
                    </p>
                  </div>

                  {/* Current Password */}
                  <div>
                    <label className={`text-[10px] font-bold uppercase ${mutedClass} block mb-1`}>Current Password</label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={profileData.currentPassword}
                        onChange={(e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className={`w-full px-3 py-2.5 pr-10 rounded-xl border ${borderClass} ${surfaceClass} ${textClass} text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10 transition-all outline-none`}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg ${mutedClass} ${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} transition-all duration-200`}
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label className={`text-[10px] font-bold uppercase ${mutedClass} block mb-1`}>New Password</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={profileData.newPassword}
                        onChange={(e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
                        className={`w-full px-3 py-2.5 pr-10 rounded-xl border ${borderClass} ${surfaceClass} ${textClass} text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10 transition-all outline-none`}
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg ${mutedClass} ${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} transition-all duration-200`}
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className={`text-[10px] font-bold uppercase ${mutedClass} block mb-1`}>Confirm New Password</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={profileData.confirmPassword}
                        onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className={`w-full px-3 py-2.5 pr-10 rounded-xl border ${profileData.confirmPassword && profileData.newPassword !== profileData.confirmPassword ? 'border-red-500' : borderClass} ${surfaceClass} ${textClass} text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10 transition-all outline-none`}
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg ${mutedClass} ${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} transition-all duration-200`}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {profileData.confirmPassword && profileData.newPassword !== profileData.confirmPassword && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Passwords do not match
                      </p>
                    )}
                    {profileData.confirmPassword && profileData.newPassword === profileData.confirmPassword && profileData.newPassword && (
                      <p className="text-xs text-emerald-500 mt-1 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Passwords match
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`px-4 sm:px-6 py-3 border-t ${borderClass} flex items-center justify-end gap-2 ${darkMode ? 'bg-primaryB-800/50' : 'bg-primaryB-50'}`}>
              <button
                onClick={() => setShowProfileModal(false)}
                className={`px-5 py-2 ${surfaceClass} border ${borderClass} rounded-xl text-xs font-bold ${textClass} ${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} transition-all duration-200`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={profileLoading}
                className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl text-xs font-bold shadow-elevated shadow-amber-500/25 disabled:opacity-50 flex items-center justify-center gap-1.5 transition-all duration-200"
              >
                {profileLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerLayout;
